import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { Box, Stack, Text, Tooltip } from '@ltvco/refresh-lib/theme';
import { CollectionType, OptionType } from '@ltvco/refresh-lib/v2';
import { TuneRounded, Gavel } from '@mui/icons-material';
import { useContext, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { NavButton, OptionResults } from 'routes/SearchPerson/components';

import { COURT_CASES } from '../constants';

import { SearchHeaderContainerCarousel } from './components';

interface SearchHeaderCarouselProps {
  options: OptionType[];
  selectedOption: CollectionType | typeof COURT_CASES;
  // eslint-disable-next-line no-unused-vars
  setSelectedOption: (value: OptionType) => void;
  isInMobileFiltersAndTabsVariation?: boolean;
  // eslint-disable-next-line no-unused-vars
  setParentSidebarCollapsed?: (value: boolean) => void;
  isChatPage?: boolean;
}

const SearchHeaderCarousel = ({
  options,
  selectedOption,
  setSelectedOption,
  isInMobileFiltersAndTabsVariation,
  setParentSidebarCollapsed,
  isChatPage,
}: SearchHeaderCarouselProps) => {
  const { trackEventGA4 } = useContext(AppConfig);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const container = scrollContainerRef.current;
    const selectedButton = optionRefs.current[selectedOption];
    if (isChatPage && selectedOption === CollectionType.PUBLIC_RECORDS) {
      return;
    }

    if (container && selectedButton) {
      const containerRect = container.getBoundingClientRect();
      const buttonRect = selectedButton.getBoundingClientRect();
      const offset =
        buttonRect.left - containerRect.width / 2 + buttonRect.width / 2;

      container.scrollBy({
        left: offset,
        behavior: 'smooth',
      });
    }
  }, [selectedOption]);

  const handleSelectTab = (option: OptionType) => {
    if (option.value !== selectedOption) {
      setSelectedOption(option);
    }
  };

  const getFiltersCount = () => {
    const params = {
      fullname: searchParams.get('fullname'),
      fname: searchParams.get('fname'),
      ln: searchParams.get('ln'),
      mn: searchParams.get('mn'),
      age: searchParams.get('age'),
      city: searchParams.get('city'),
      state: searchParams.get('state'),
      country: searchParams.get('country'),
      birthYear: searchParams.get('birthYear'),
      deathYear: searchParams.get('deathYear'),
      birthMonth: searchParams.get('birthMonth'),
      deathMonth: searchParams.get('deathMonth'),
      title: searchParams.get('title'),
      company: searchParams.get('company'),
      level: searchParams.get('level'),
      industry: searchParams.get('industry'),
      company_size_max: searchParams.get('companySizeMax'),
      company_size_min: searchParams.get('companySizeMin'),
      address: searchParams.get('address'),
    };

    return Object.values(params).filter(Boolean).length;
  };

  return (
    <SearchHeaderContainerCarousel
      className="navbar"
      top={isInMobileFiltersAndTabsVariation || isChatPage ? '0' : '150px'}
    >
      <Box
        ref={scrollContainerRef}
        sx={{
          display: 'flex',
          overflowX: 'auto',
          scrollBehavior: 'smooth',
          '::-webkit-scrollbar': { display: 'none' },
          flexGrow: 1,
          whiteSpace: 'nowrap',
          gap: '4px',
          position: 'relative',
        }}
      >
        {' '}
        {isInMobileFiltersAndTabsVariation && setParentSidebarCollapsed && (
          <NavButton
            onClick={() => {
              trackEventGA4({
                event_name: 'search_results_interaction',
                type: 'open_filter',
              });
              setParentSidebarCollapsed(false);
            }}
            className="filters-button"
            sx={{
              position: 'sticky',
              left: '0',
              zIndex: 100,
            }}
          >
            <TuneRounded sx={{ fontSize: '20px' }} />
            <Text variant="body1" m={0} noWrap>
              ({getFiltersCount()})
            </Text>
          </NavButton>
        )}
        {options
          .filter((option) => option.visible)
          .map((option) => {
            const button = (
              <NavButton
                ref={(el) => (optionRefs.current[option.value] = el)}
                onClick={() => handleSelectTab(option)}
                className={selectedOption === option.value ? 'active' : ''}
                sx={{ marginRight: '2px' }}
              >
                <Stack flexDirection="row" mt={0} alignItems="center">
                  <OptionResults
                    className="options-results"
                    sx={{
                      width: parseInt(option.length) > 9 ? '40px' : '25px',
                      marginRight: '4px',
                    }}
                  >
                    {option.value === COURT_CASES ? (
                      <Gavel sx={{ fontSize: '16px' }} />
                    ) : option.length ? (
                      option.length
                    ) : (
                      '0'
                    )}
                  </OptionResults>
                  <Text variant="body1" m={0} noWrap>
                    {option.label}
                  </Text>
                </Stack>
              </NavButton>
            );

            return (
              <Tooltip
                key={option.value}
                title={option.tooltipContent}
                leaveTouchDelay={5000}
                slotProps={{
                  tooltip: {
                    style: {
                      marginTop: 8,
                    },
                  },
                }}
              >
                {button}
              </Tooltip>
            );
          })}
      </Box>
    </SearchHeaderContainerCarousel>
  );
};

export default SearchHeaderCarousel;
